import type { DocumentEntry } from './useDocuments';

/**
 * 목록 줄 세우기 — <b>자료실 화면과 도크가 같은 규칙을 나눠 쓴다</b>.
 *
 * <p>두 벌로 두면 같은 폴더가 화면마다 다른 순서로 보인다. 「크게 보기」로 도크에서 화면으로 넘어가는
 * 길이 있는 이상(<code>StorageDock</code>), 그 순간 순서가 바뀌면 사람은 목록이 달라진 줄 안다.
 *
 * <p>정렬은 <b>보고 있는 폴더 안</b>에서만 한다(하위를 훑지 않는다). 서버에 시키지 않는 이유는
 * 목록이 이미 그 폴더 전부이기 때문이다 — 왕복을 한 번 더 하면서 얻을 것이 없다.
 */

export type SortKey = 'name' | 'size' | 'modified';

export type DocSort = { key: SortKey; dir: 'asc' | 'desc' };

export const DEFAULT_SORT: DocSort = { key: 'name', dir: 'asc' };

/**
 * 이름 비교는 <b>한국어 사전순 + 숫자 인식</b>이다. 코드포인트로 비교하면 `보고서10` 이 `보고서2`
 * 앞에 서고(문자 '1' < '2'), 자료실은 회차·날짜가 이름에 박히는 곳이라 그 순서가 곧 오답이다.
 */
const NAME_COLLATOR = new Intl.Collator('ko', { numeric: true, sensitivity: 'base' });

/**
 * 같은 칸을 다시 누르면 방향이 뒤집히고, 다른 칸으로 옮기면 <b>그 칸에서 가장 쓸모 있는 방향</b>으로
 * 시작한다 — 이름은 가나다순, 크기·날짜는 큰 것·최근 것부터다. 크기를 누르는 사람이 찾는 것은
 * 대개 «제일 큰 파일» 이지 0 바이트 파일이 아니다.
 */
export function nextSort(prev: DocSort, key: SortKey): DocSort {
  if (prev.key === key) {
    return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
  }
  return { key, dir: key === 'name' ? 'asc' : 'desc' };
}

/**
 * <h2>폴더는 방향과 무관하게 위다</h2>
 * 폴더는 «크기 3.2MB» 같은 값을 원래 갖고 있지 않고, 나중에 집계가 와서 채워진다
 * ({@code useS3FolderStats}). 그것을 파일과 섞어 세우면 정렬할 때마다 폴더가 목록 곳곳으로 흩어져
 * 파고들 길을 눈으로 다시 찾아야 한다 — 탐색기가 폴더를 위에 붙여 두는 이유와 같다.
 *
 * <h2>값이 없는 행은 아래로</h2>
 * 크기·수정일은 비어 있을 수 있다(아직 안 센 폴더 · 시각을 안 주는 NAS). 그것을 0 으로 치면
 * 오름차순에서 '—' 만 잔뜩 얹힌 화면이 되어 «정렬이 안 먹었다» 로 읽힌다. 방향과 무관하게 뒤로
 * 보내고, 값이 같은 것끼리는 이름으로 가른다(같은 자료를 다시 눌러도 순서가 안 흔들리게).
 */
export function sortEntries(entries: DocumentEntry[], sort: DocSort): DocumentEntry[] {
  const sign = sort.dir === 'asc' ? 1 : -1;
  return [...entries].sort((a, b) => {
    if (a.directory !== b.directory) return a.directory ? -1 : 1;
    if (sort.key === 'name') return sign * NAME_COLLATOR.compare(a.name, b.name);
    const left = sortValue(a, sort.key);
    const right = sortValue(b, sort.key);
    if (left == null || right == null) {
      if (left == null && right == null) return NAME_COLLATOR.compare(a.name, b.name);
      return left == null ? 1 : -1;
    }
    return left === right ? NAME_COLLATOR.compare(a.name, b.name) : sign * (left - right);
  });
}

function sortValue(entry: DocumentEntry, key: SortKey): number | null {
  if (key === 'size') return entry.size ?? null;
  if (!entry.modifiedAt) return null;
  const time = new Date(entry.modifiedAt).getTime();
  return Number.isNaN(time) ? null : time;
}
